<template>
  <div>
    <template v-if="!(isEdit && store.loading) && !store.error">
      <v-container>
        <v-form v-model="formValid" :ref="form">
          <SelectContactKind v-model="contactHistory.Art" :show-label="true" :rules="[ruleRequired]"/>
          <SelectContact v-model="contactHistory.Ansprechpartner" :kunden-nr="Number($route.params.id)"
                         :show-label="true"
                         :rules="[ruleRequired]"/>
          <v-text-field v-model="contactHistory.Thema" label="Thema" :rules="[ruleRequired, ruleMaxLen(80)]"/>
          <v-textarea v-model="contactHistory.Inhalt" label="Inhalt"/>
          <SelectDispatcher v-model="contactHistory.Disponent" class="mt-n4 mb-6" :show-label="true"/>
          <!--<v-text-field v-model="contactHistory.Prioritaet" label="Priorität" type="number" />-->
          <!--<v-checkbox v-model="showResubmission" hide-details label="Wiedervorlage" />
          <v-text-field v-if="showResubmission" type="datetime-local" v-model="Wiedervorlage" label="Wiedervorlage" />
          -->
          <CreateBedieners v-model="Bedieners"/>
          <v-btn color="primary" @click="save" :disabled="!formValid" :loading="loadingSave">Speichern</v-btn>
        </v-form>
      </v-container>
    </template>
    <template v-else-if="store.error">
      <error :module="store.$id"/>
    </template>
    <v-overlay :model-value="loadingSave || (isEdit && store.loading)" class="align-center justify-center" persistent>
      <v-card>
        <v-card-text class="text-center">
          <div class="mb-4">
            <v-progress-circular indeterminate/>
          </div>
          <div v-if="(isEdit && store.loading)" class="mt-4">Kontakt&shy;erfassung wird geladen</div>
          <div v-if="loadingSave" class="mt-4">Kontakt wird gespeichert</div>
        </v-card-text>
      </v-card>
    </v-overlay>
  </div>
</template>

<script lang="ts" setup>
import {nextTick, ref, watch} from 'vue';
import Error from '@/components/Error.vue';
import SelectContactKind from '@/components/SelectContactKind.vue';
import SelectContact from '@/components/SelectContact.vue';
import SelectDispatcher from '@/components/SelectDispatcher.vue';
import CreateBedieners from '@/components/Customer/CreateBedienerComponent.vue';
import router from '@/router';
import {doAlert} from '@/util';
import {postContactHistory, putContactHistory} from '@/api/module/contactHistory';
import {useStorage} from '@/composables/storage';
import ContactHistory, {ApiContactHistoryBedienerSubmitData, ApiContactHistorySubmitData} from '@/model/ContactHistory';
import moment from 'moment';
import {useRoute} from 'vue-router';
import {useSettings} from '@/store/settings';
import {postContactBediener, putContactBediener, deleteContactBediener} from '@/api/module/followup';
import useDetailContactHistoryStore from '@/store/detailContactHistory';
import {VNodeRef} from 'vue';

const store = useDetailContactHistoryStore();
const route = useRoute();
const isEdit = route.params.contact_history_id !== undefined;

useStorage(
    store,
    (contactHistory) => {
      return contactHistory ?
      `Kontakt bearbeiten` :
      'Kontakt anlegen';
    },
    (route) => {
      return {
        KundenNr: parseInt(Array.isArray(route.params.id) ? route.params.id[0] : route.params.id),
        Nr: parseInt(Array.isArray(route.params.contact_history_id) ? route.params.contact_history_id[0] : route.params.contact_history_id),
      };
    },
);

const contactHistory = ref<ApiContactHistorySubmitData>({
  Art: '',
  Ansprechpartner: [
    {AnsprechpartnerNr: 0},
  ],
  Disponent: null,
  Thema: '',
  Inhalt: '',
});


const Bedieners = ref<Array<ApiContactHistoryBedienerSubmitData & { apiGiven: boolean, changed: boolean }>>([]);
let originalBedieners: Array<ApiContactHistoryBedienerSubmitData & { apiGiven: boolean, changed: boolean }> = [];

const currentTime = moment();
const remainder = 15 - (currentTime.minute() % 15);

// Standard Benutzer aktueller Nutzer / aktuelles Datum
Bedieners.value = [
  {
    Bediener: useSettings().userShort ?? '',
    WiedervorlageDatum: moment().format('YYYY-MM-DD'),
    WiedervorlageUhrzeit: moment(currentTime).add(remainder, 'minutes').format('HH:mm'),
    Erledigt: false,
    Gelesen: false,
    apiGiven: true,
    changed: false,
  },
];

const showResubmission = ref(false);

const loadingSave = ref(false);
const formValid = ref(false);
const form = ref<HTMLFormElement & VNodeRef | undefined>();

watch(route.query, () => {
  if (route?.query.ansprechpartner_nr) {
    contactHistory.value.Ansprechpartner[0].AnsprechpartnerNr = Number(route.query.ansprechpartner_nr);
  }
}, {immediate: true});

watch(() => store.data, async (data: ContactHistory | null) => {
  if (!isEdit || !data) return;
  contactHistory.value.Art = data.Art ?? '';
  contactHistory.value.Ansprechpartner = data.Ansprechpartner.length > 0 ?
    data.Ansprechpartner.map((partn) => {
      return {AnsprechpartnerNr: partn.AnsprechpartnerNr};
    }) : [];
  contactHistory.value.Disponent = data.Disponent ?? '';
  contactHistory.value.Thema = data.Thema ?? '';
  contactHistory.value.Inhalt = data.Inhalt ?? '';

  Bedieners.value = data.Bediener.map((bed) => ({
    ...bed,
    changed: false,
    WiedervorlageDatum: moment(bed.WiedervorlageDatum).format('YYYY-MM-DD'),
    WiedervorlageUhrzeit: bed.WiedervorlageUhrzeit ? moment(bed.WiedervorlageUhrzeit).format('HH:mm') : null!,
    Gelesen: bed.Gelesen.Status,
    Erledigt: bed.Erledigt.Status,
    apiGiven: true,
  }));
  originalBedieners = data.Bediener.map((bed) => ({
    ...bed,
    changed: false,
    WiedervorlageDatum: moment(bed.WiedervorlageDatum).format('YYYY-MM-DD'),
    WiedervorlageUhrzeit: bed.WiedervorlageUhrzeit ? moment(bed.WiedervorlageUhrzeit).format('HH:mm') : null!,
    Gelesen: bed.Gelesen.Status,
    Erledigt: bed.Erledigt.Status,
    apiGiven: true,
  }));
  await nextTick();
  form.value?.validate();
}, {immediate: true});

watch(
    form, () => {
      form.value?.validate();
    },
);

async function saveBedieners(contactHistory: ContactHistory, isNewContact: boolean) {
  const kundennr = contactHistory.KundenNr;
  const kontaktnr = contactHistory.Nr;
  const operations: Promise<void>[] = [];
  for (const originalBediener of originalBedieners) {
    if (Bedieners.value.some((newBediener) => originalBediener.Bediener === newBediener.Bediener)) continue;
    // delete
    await deleteContactBediener({kundennr, kontaktnr, bediener: originalBediener.Bediener});
  }

  for (const newBediener of Bedieners.value) {
    if (newBediener.Bediener == useSettings().userShort && isNewContact) continue;

    if (originalBedieners.some((originalBediener) => originalBediener.Bediener === newBediener.Bediener)) {
      if (newBediener.changed) {
        // update Bediener
        if (!newBediener.WiedervorlageUhrzeit) {
          newBediener.WiedervorlageUhrzeit = '00:00';
        }
        operations.push(putContactBediener({kundennr, kontaktnr, bediener: newBediener}));
      }
    } else {
      // create
      operations.push(postContactBediener({kundennr, kontaktnr, bediener: newBediener}));
    }
  }

  await Promise.allSettled(operations);
}

async function save() {
  try {
    loadingSave.value = true;
    form.value?.validate();
    if (!formValid.value) {
      throw new Error('Bitte alle Felder korrekt ausfüllen');
    }
    let contactHistoryTemp: ContactHistory;
    let openContactAfterFinish = false;
    // This is a workaround for ios
    const payload = JSON.parse(JSON.stringify(contactHistory.value));
    let isNewContact = false;
    if (store.data && route.params.contact_history_id) {
      contactHistoryTemp = await putContactHistory(Number(route.params.id), Number(route.params.contact_history_id), payload);
    } else {
      contactHistoryTemp = await postContactHistory(Number(route.params.id), payload);
      isNewContact = true;
      openContactAfterFinish = true;
    }

    await saveBedieners(contactHistoryTemp, isNewContact);
    await store.fetch();

    if (openContactAfterFinish) {
      router.push({
        name: 'contact_histories_details',
        params: {
          id: contactHistoryTemp.KundenNr,
          contact_history_id: contactHistoryTemp.Nr,
        },
      });
    } else {
      router.go(-2);
    }
  } catch (err: any) {
    await doAlert(err.message, 'Fehler');
  } finally {
    loadingSave.value = false;
  }
};

function ruleRequired(value: any): true | string {
  if (value) return true;
  if (value === 0) return true;

  return 'erforderlich';
}

function ruleMaxLen(maxLength: number) {
  return (value: any) => !value || String(value).length <= maxLength || `maximal ${maxLength} Zeichen`;
}

defineExpose({
  contactHistory,
  store,
  form, formValid,
  Bedieners,
  loadingSave,
  showResubmission,
  ruleRequired,
  ruleMaxLen,
  fetch,
  save,
  Error,
  SelectContactKind,
  SelectDispatcher,
  CreateBedieners,
  SelectContact,
});

</script>
